import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getDetailsForUser,
  setUserPreference,
} from "../../services/UserService";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { UserContext } from "../MainLayout";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import Icons from "../../utils/components/icons";
import DropDown from "../../utils/components/common/dropdown/dropdown";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";

function SecurityOptions() {
  const { t } = useTranslation("header");

  const { userCognitoData } = useContext(UserContext);

  const [securityOptionsData, setSecurityOptionsData] = useState();
  const [isLoading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [isTwoFAEnabled, setIsTwoFAEnabled] = useState(false);
  const [sendCodeOn, setSendCodeOn] = useState("Mobile");
  const [updateFailErr, setUpdateFailErr] = useState();
  // const [showChangePassModal, setShowChangePassModal] = useState(false);

  const getUserProfileDetails = useCallback(async () => {
    try {
      const queryParam = {
        cognitoId: userCognitoData?.username,
      };
      const response = await getDetailsForUser(queryParam);
      const data =
        response?.data?.UserDetails?.UserPreference?.SecuritySettings;
      setSecurityOptionsData(data);
      const receiveSecurityCodeOn =
        data?.ReceiveSecurityCodeOn === "Email" ? "Email" : "Mobile";
      setSendCodeOn(receiveSecurityCodeOn);
      setIsTwoFAEnabled(data?.MFA);
    } catch (err) {
      setErr(true);
    }
    setLoading(false);
  }, [userCognitoData?.username]);

  useEffect(() => {
    if (userCognitoData) {
      getUserProfileDetails();
    }
  }, [getUserProfileDetails, userCognitoData]);

  const updateMFAEnableStatus = useCallback(
    async (isMFAEnabled) => {
      setLoading(true);
      try {
        const queryParam = {
          cognitoId: userCognitoData?.username,
          attributeName: "SecuritySettings",
          keyName: "MFA",
          keyValue: isMFAEnabled,
        };
        await setUserPreference(queryParam);
        setIsTwoFAEnabled(isMFAEnabled);
      } catch (err) {
        setUpdateFailErr("genericErrMsg");
      }
      setLoading(false);
    },
    [userCognitoData]
  );

  const updateSecurityCodeOption = useCallback(
    async (val) => {
      setLoading(true);
      try {
        const queryParam = {
          cognitoId: userCognitoData?.username,
          attributeName: "SecuritySettings",
          keyName: "ReceiveSecurityCodeOn",
          keyValue: val,
        };
        await setUserPreference(queryParam);
        setSendCodeOn(val);
      } catch (err) {
        if (err?.response?.data?.message === "mfa disabled") {
          setUpdateFailErr("twoFADatachangedMsg");
          getUserProfileDetails();
        } else {
          setUpdateFailErr("genericErrMsg");
        }
      }
      setLoading(false);
    },
    [getUserProfileDetails, userCognitoData?.username]
  );

  const onErrPopupclose = useCallback(() => {
    setUpdateFailErr("");
  }, []);

  // const changePassClicked = useCallback(() => {
  //   setShowChangePassModal(true);
  //   hideEditProfileModal(true);
  // }, []);

  return (
    <div className="security-options-container component">
      {securityOptionsData && !err ? (
        <>
          {/* Header Section Starts */}
          <div className="heading-h2 header-section heading">
            {t("UserPref.navOptions.security")}
          </div>
          {/* Header Section Ends */}

          {/* Body Section Starts */}
          <div>
            {/* Change Password row starts */}
            <div
              className="options-container change-pass-row"
              // onClick={changePassClicked}
            >
              <div className="d-flex gap-2">
                <div>{Icons.changePass}</div>
                <div className="heading-h4 change-pass">
                  {t("security.optionChangePass")}
                </div>
              </div>
              <div className="arrow">{Icons.chevronIconRightPurple}</div>
            </div>
            {/* Change Password row ends */}

            {/* Toggle Two FA row starts */}
            <div className="options-container">
              <div className="d-flex gap-2">
                <div>{Icons.twoFA}</div>
                <div className="heading-h4 two-FA">
                  {t("security.option2FA")}
                </div>
              </div>
              <div className="form-check form-switch" id="enableTwoFA">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="enableTwoFACheckbox"
                  checked={isTwoFAEnabled}
                  onChange={() => updateMFAEnableStatus(!isTwoFAEnabled)}
                />
              </div>
            </div>
            {/* Toggle Two FA row starts */}

            {/* Send Code on row starts */}
            <div className="options-container receive-code-on">
              <div className="d-flex gap-2">
                <div>{Icons.securityCode}</div>
                <div className="heading-h4 receive-on">
                  {t("security.optionReceiveOn")}
                </div>
              </div>
              <div>
                <DropDown
                  options={[
                    { label: t("security.mobile"), value: "Mobile" },
                    { label: t("security.email"), value: "Email" },
                  ]}
                  hideSelectedOption
                  wrapperClass={isTwoFAEnabled ? "primary" : "greyLight"}
                  autoid="security-code-option"
                  value={sendCodeOn}
                  onSelection={(val) => {
                    if (isTwoFAEnabled) {
                      updateSecurityCodeOption(val);
                    }
                  }}
                  disabled={!isTwoFAEnabled}
                  alwaysShowIcon={true}
                />
              </div>
            </div>
            {/* Send Code on row ends */}
          </div>
          {/* Body Section Ends */}
        </>
      ) : (
        <></>
      )}
      {!isLoading && err && (
        <>
          <div className="heading-h2 header-section heading">
            {t("UserPref.navOptions.security")}
          </div>
          <div className="empty-screen-container">
            <EmptyScreen
              img={Icons.userPref.noData}
              small={true}
              title={t("UserPref.noDataTitle")}
              desc={t("UserPref.noDataDescription")}
            />
          </div>
        </>
      )}
      {isLoading && <LoadingSpinner />}
      {/* Err Popup */}
      <ModalComponent
        idName="security-error-modal"
        isOpen={updateFailErr}
        onCloseModal={onErrPopupclose}
        descriptionText={t(updateFailErr)}
      />

      {/* Change Password Modal */}
      {/* {showChangePassModal && (
        <ChangePassword
          hideEditProfileModal={hideEditProfileModal}
          open={showChangePassModal}
        />
      )} */}
    </div>
  );
}

export default SecurityOptions;
